/** @file Maps document ids to compiled indices (bones, slots, by name) and builds the bone pick mask. */
import type { SkeletonData } from '@pose2d/core/types';
import type { Doc, Id } from '@pose2d/format/types';

/** Index ↔ id tables of one compiled rig; `pickable[i]` is 1 unless the bone is hidden or locked (R-E1-11). */
export interface SceneMaps {
  boneIds: Id[];
  boneIndex: Map<Id, number>;
  slotIds: Id[];
  slotIndex: Map<Id, number>;
  pickable: Uint8Array;
}

/** Names are unique per kind (§5.1), so a compiled name finds its document id. */
export function sceneMaps(doc: Doc, data: SkeletonData): SceneMaps {
  const boneByName = new Map(Object.entries(doc.bones).map(([id, b]) => [b.name, id]));
  const slotByName = new Map(Object.entries(doc.slots).map(([id, s]) => [s.name, id]));
  const boneIds = data.bones.map((b) => boneByName.get(b.name) ?? '');
  const slotIds = data.slots.map((s) => slotByName.get(s.name) ?? '');
  const shut = (id: Id): boolean => doc.bones[id]?.ui?.hidden === true || doc.bones[id]?.ui?.locked === true;
  return {
    boneIds, boneIndex: new Map(boneIds.map((id, i) => [id, i])), slotIds, slotIndex: new Map(slotIds.map((id, i) => [id, i])),
    pickable: Uint8Array.from(boneIds, (id) => (shut(id) ? 0 : 1)),
  };
}
